import { DataProvider } from "ra-core";
import {
  ComplianceOrg,
  ComplianceOrgCallbacks,
  ComplianceOrgList,
  ComplianceDataset,
  ComplianceProFailed,
  ComplianceQueryParams,
  ComplianceSkippedRead,
  PatientExemption,
  PatientExemptions,
  PatientRow,
  StreamCoverage,
  ProjectComplianceConfig
} from "./complianceTypes";
import { defaultMedBasis, type MissingDataStream } from "./complianceFilters";
import { dateDaysBefore, daysSince, localDate } from "./complianceClock";
import {
  COMPLIANCE_METRIC_CATEGORIES,
  type ComplianceConfig,
  type ComplianceExemptionData,
  type ComplianceExemptionReason,
  type ComplianceMetricCategory,
  type ComplianceOrgData,
  type ComplianceProjectData,
  type ComplianceProjectPatientData
} from "../data/buildQueryCompliance";
import { filterPatientAccess } from "../data/buildQueryProjectPatient";
import { fetchCompliancePro, type ParsedProPatient } from "./complianceProApi";

const CATEGORY_TO_STREAM: Record<ComplianceMetricCategory, MissingDataStream> =
  {
    TREMOR_DYSKINESIA: "tremor_dyskinesia",
    MOBILITY: "mobility",
    SLEEP: "sleep",
    TAP_TEST: "tap_test"
  };

// For a patient without a Strive time zone, and for the project-wide window.
const UTC = "UTC";

const WINDOW_DAYS = 14;

function epochToIso(epochSec: number): string {
  return new Date(epochSec * 1000).toISOString();
}

function epochToLocalDate(epochSec: number, timezone: string): string {
  return localDate(new Date(epochSec * 1000), timezone);
}

function categorizeOrg(orgTags: string[] | null): ComplianceOrg["category"] {
  const tags = orgTags ?? [];
  if (tags.includes("PHARMA")) return "pharma";
  if (tags.includes("CLINICAL") || tags.includes("CARE_MANAGEMENT")) {
    return "health_system";
  }
  return "other";
}

export function toProjectConfig(
  config: ComplianceConfig
): ProjectComplianceConfig {
  return {
    projectId: config.projectId,
    categories: config.categories as MissingDataStream[],
    complianceThresholdDays: config.complianceThresholdDays,
    atRiskThresholdDays: config.atRiskThresholdDays,
    outreachGapDays: config.outreachGapDays,
    maxOutreachAttempts: config.maxOutreachAttempts ?? null,
    studyMedications: config.studyMedications
  };
}

const EXEMPTION_REASON_LABELS: Record<ComplianceExemptionReason, string> = {
  medical: "Medical",
  personal: "Personal",
  non_responsive: "Non-responsive"
};

export function exemptionReason(exemption: ComplianceExemptionData): string {
  const label = EXEMPTION_REASON_LABELS[exemption.reason] ?? exemption.reason;
  return exemption.detail ? `${label}: ${exemption.detail}` : label;
}

export function toExemption(
  exemption: ComplianceExemptionData
): PatientExemption {
  return {
    streams: exemption.categories,
    reason: exemptionReason(exemption)
  };
}

// Recency is when the device recorded the data, not when it synced.
export function toStreamCoverage(
  name: MissingDataStream,
  lastDataAt: string | null,
  timezone: string,
  now: Date
): StreamCoverage {
  return {
    name,
    daysSinceLastData: daysSince(lastDataAt, timezone, now),
    exempt: false,
    exemptWhy: null
  };
}

// A patient's PRO data. If null: the PRO response left them out (no Strive user), so with no
// medicines they're flagged for having no medication schedule. If "failed": their batch failed.
export type PatientPro = ParsedProPatient | null | "failed";

// Combines a patient's CarrotGraph and PRO data into one row.
export function toPatientRow(
  projectPatient: ComplianceProjectPatientData,
  pro: PatientPro,
  projectId: string,
  config: Omit<ProjectComplianceConfig, "projectId">,
  now: Date,
  exemption: PatientExemption | null = null
): PatientRow {
  const { patient } = projectPatient;
  const proPatient = pro !== "failed" ? pro : null;
  // Every date and recency count is on the patient's calendar.
  const timezone = proPatient?.timezone ?? UTC;
  const today = localDate(now, timezone);

  // The window starts at org access, matching the Start Date in the project's patient list. A
  // patient with org access long before joining the project is therefore monitored from the
  // earlier date; if monitoring should start when they join, use the later of the two here and
  // in buildQueryProjectPatient. Without a matching access, they're monitored from when they
  // joined the project rather than from a made-up date.
  const access = filterPatientAccess(
    patient.patientAccessList?.patientAccess,
    projectPatient.organizationId
  );
  const windowStart = access?.startTime ?? projectPatient.createdAt;

  const streams = COMPLIANCE_METRIC_CATEGORIES.map((category) => {
    const endTime = projectPatient.metricCategoryTimeRanges.find(
      (tr) => tr.metricCategory === category
    )?.endTime;
    return toStreamCoverage(
      CATEGORY_TO_STREAM[category],
      endTime ? epochToIso(endTime) : null,
      timezone,
      now
    );
  });

  return {
    patientId: patient.id,
    projectId,
    codeName: projectPatient.codeName ?? patient.codeName,
    codeNameMissing: !projectPatient.codeName,
    // Set by computeTriage in the overview.
    triage: "steady",
    triageReasons: [],
    flaggedChecks: 0,
    longestSilenceDays: null,
    medications: proPatient?.medications ?? [],
    proLoaded: pro !== "failed",
    medRecent: {
      basis: defaultMedBasis(config),
      hasSchedule: false,
      lastLoggedAt: null,
      daysSinceLastData: null,
      autologOn: false
    },
    bowelMovementRecent: {
      daysSinceLastData: daysSince(
        proPatient?.bowelMovementLastLoggedAt ?? null,
        timezone,
        now
      )
    },
    streams,
    timezone,
    timezoneMissing: proPatient?.timezoneMissing ?? true,
    contact: { state: "unknown", gapDays: null, attempts: 0 },
    exemption,
    window: {
      start: epochToLocalDate(windowStart, timezone),
      end: access?.endTime ? epochToLocalDate(access.endTime, timezone) : today,
      isActive: access?.isActive ?? true
    },
    appMode: patient.appMode?.displayName ?? null,
    createdAt: epochToLocalDate(
      patient.createdAt ?? projectPatient.createdAt,
      timezone
    ),
    lastOutreachDate: null
  };
}

async function fetchConfigs(
  dataProvider: DataProvider
): Promise<ComplianceConfig[]> {
  const result = await dataProvider.getList("ProjectComplianceConfig", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "id", order: "ASC" },
    filter: {}
  });
  return result.data as ComplianceConfig[];
}

// Drops the requests that fail and reports each one. The config list isn't filtered by
// access, while project and org reads are, so one project the user can't read mustn't hide
// the ones they can.
async function settled<T>(
  resource: "ComplianceProject" | "ComplianceOrg",
  ids: string[],
  read: (id: string) => Promise<T>,
  onSkipped?: ComplianceSkippedRead
): Promise<T[]> {
  const results = await Promise.all(
    ids.map((id) =>
      read(id).then(
        (value) => ({ ok: true as const, value }),
        (error: unknown) => {
          onSkipped?.(resource, id, error);
          return { ok: false as const };
        }
      )
    )
  );
  return results.flatMap((r) => (r.ok ? [r.value] : []));
}

// Only projects with a compliance config are monitored, so they make up the org list.
// Exemptions aren't per project, so they're loaded once here rather than with each project.
export async function fetchOrgs(
  dataProvider: DataProvider,
  { onSkipped, onExemptionsFailed }: ComplianceOrgCallbacks = {}
): Promise<ComplianceOrgList> {
  // Exemptions need Patient read-all in carrotgraph, so a failure there still lists the
  // orgs, just without exemptions.
  const exemptionsRead = fetchExemptions(dataProvider).catch(
    (error: unknown) => {
      onExemptionsFailed?.(error);
      return null;
    }
  );
  const orgs = await fetchOrgList(dataProvider, onSkipped);
  return { orgs, exemptions: await exemptionsRead };
}

async function fetchOrgList(
  dataProvider: DataProvider,
  onSkipped?: ComplianceSkippedRead
): Promise<ComplianceOrg[]> {
  const configs = await fetchConfigs(dataProvider);
  const configByProject = new Map(configs.map((c) => [c.projectId, c]));
  const projects = await settled(
    "ComplianceProject",
    Array.from(configByProject.keys()),
    async (id) => {
      const { data } = await dataProvider.getOne("ComplianceProject", { id });
      return data as ComplianceProjectData;
    },
    onSkipped
  );

  // Keyed by the project's organizationId, so grouping doesn't depend on the org ID format.
  const orgs = await settled(
    "ComplianceOrg",
    Array.from(new Set(projects.map((p) => p.organizationId))),
    async (id) => {
      const { data } = await dataProvider.getOne("ComplianceOrg", { id });
      return { requestedId: id, org: data as ComplianceOrgData };
    },
    onSkipped
  );

  return orgs.map(({ requestedId, org }) => {
    const orgProjects = projects
      .filter((p) => p.organizationId === requestedId)
      .flatMap((p) => {
        const config = configByProject.get(p.id);
        if (!config) return [];
        return [
          {
            id: p.id,
            displayName: p.title,
            orgId: org.id,
            patientCount: p.totalProjectPatientCount,
            config: toProjectConfig(config)
          }
        ];
      });
    return {
      id: org.id,
      displayName: org.displayName,
      category: categorizeOrg(org.orgTags),
      totalPatients: orgProjects.reduce((sum, p) => sum + p.patientCount, 0),
      projects: orgProjects
    };
  });
}

// Follows the cursor to the end; the query sets the page size.
async function fetchAllPages<T>(
  dataProvider: DataProvider,
  resource: string,
  filter: Record<string, unknown>
): Promise<T[]> {
  const rows: T[] = [];
  let nextPageCursor: string | null = null;
  do {
    const result: { data: unknown[]; endCursor?: string | null } =
      await dataProvider.getList(resource, {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "id", order: "ASC" },
        filter,
        meta: { nextPageCursor }
      });
    rows.push(...(result.data as T[]));
    nextPageCursor = result.endCursor ?? null;
  } while (nextPageCursor);
  return rows;
}

// carrotgraph returns at most 1000 patients per page.
function fetchProjectPatients(
  dataProvider: DataProvider,
  projectId: string
): Promise<ComplianceProjectPatientData[]> {
  return fetchAllPages(dataProvider, "ComplianceProjectPatient", {
    projectId
  });
}

// carrotgraph returns at most 100 exemptions per page, and at most one per patient.
async function fetchExemptions(
  dataProvider: DataProvider
): Promise<PatientExemptions> {
  const exemptions = await fetchAllPages<ComplianceExemptionData>(
    dataProvider,
    "ComplianceExemption",
    {}
  );
  return new Map(exemptions.map((e) => [e.patientId, toExemption(e)]));
}

// May need to speed up this query
// (e.g. backend PR to batch appMode and
// patientAccessList under projectPatientList)
export async function fetchComplianceData(
  dataProvider: DataProvider,
  params: ComplianceQueryParams,
  onProFailed?: ComplianceProFailed,
  now = new Date()
): Promise<ComplianceDataset> {
  // Fetch the project's patient list, which includes
  // stream recency data (eg latest timestamp of recorded T&D data).
  const projectPatients = await fetchProjectPatients(
    dataProvider,
    params.projectId
  );
  const { projectConfig, exemptions } = params;

  // Fetch PRO recency data (eg last medication log).
  // Waits for PRO so the list appears once, with each patient's time zone, rather than
  // reordering when medication and bowel movements arrive.
  const batches = await fetchCompliancePro(
    projectPatients.map((projectPatient) => projectPatient.patient.id)
  );
  const proById = new Map<string, ParsedProPatient>();
  const failedIds = new Set<string>();
  for (const batch of batches) {
    if ("error" in batch) {
      onProFailed?.(batch.patientIds, batch.error);
      batch.patientIds.forEach((id) => failedIds.add(id));
    } else {
      batch.patients.forEach((p) => proById.set(p.patientId, p));
    }
  }

  // Parse the fetched compliance data into rows for the patient list view
  const patients = projectPatients.map((projectPatient) => {
    const { id } = projectPatient.patient;
    return toPatientRow(
      projectPatient,
      failedIds.has(id) ? "failed" : proById.get(id) ?? null,
      params.projectId,
      projectConfig,
      now,
      exemptions?.get(id) ?? null
    );
  });

  const windowEnd = dateDaysBefore(localDate(now, UTC), 1);
  return {
    windowStart: dateDaysBefore(windowEnd, WINDOW_DAYS - 1),
    windowEnd,
    patients,
    proFailedCount: failedIds.size
  };
}

export interface FetchPatientSidebarParams {
  patientId: string;
  orgId: string;
  projectId: string;
  startDate: string; // YYYY-MM-DD
  // Without one, Tangerine ends on the patient's local today.
  endDate?: string;
}
